/**
 * Pure rule evaluation: (rulepack, world) → **RestrictionIntent** (`EnforcementPlan` shape).
 * The app declares intent; iOS/Android system layers may apply it within platform limits.
 *
 * **Interpreter contract:** `runAppLockboxEvaluation` is the only per-app rule walk. `evaluateLockbox`,
 * decision traces, and tests must call it (directly or via `evaluateLockbox`) so intent never drifts
 * from observability. Swift/native code is **execution** (tokens, shields); this module is **prediction**.
 */

import {
  restrictionEnforceableTargetsForAppIds,
  restrictionBundleHintsForAppIds,
} from "./restrictionTargets";

/** @typedef {import('./types.js').LockboxRulepack} LockboxRulepack */
/** @typedef {import('./types.js').EvaluationWorld} EvaluationWorld */
/** @typedef {import('./types.js').EnforcementPlan} EnforcementPlan */
/** @typedef {import('./types.js').LockboxAppEntity} LockboxAppEntity */
/** @typedef {import('./types.js').BehavioralCategory} BehavioralCategory */

/**
 * Minutes since midnight local `date`.
 * @param {Date} date
 */
function minutesSinceMidnight(date) {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * Whether [start,end) wraps midnight; if so, "in window" means >= start OR < end.
 * @param {number} startM
 * @param {number} endM
 * @param {number} nowM
 */
function inTimeWindow(startM, endM, nowM) {
  if (startM <= endM) {
    return nowM >= startM && nowM < endM;
  }
  return nowM >= startM || nowM < endM;
}

/**
 * @param {EnforcementPlan} plan
 * @param {LockboxAppEntity[]} apps
 * @returns {EnforcementPlan}
 */
function attachRestrictionTargets(plan, apps) {
  return {
    ...plan,
    restrictTargets: restrictionEnforceableTargetsForAppIds(plan.blockAppIds, apps),
    allowTargets: restrictionEnforceableTargetsForAppIds(plan.allowAppIds, apps),
    restrictBundleHints: restrictionBundleHintsForAppIds(plan.blockAppIds, apps),
    allowBundleHints: restrictionBundleHintsForAppIds(plan.allowAppIds, apps),
  };
}

/**
 * @param {LockboxRulepack} pack
 * @param {EvaluationWorld} world
 * @returns {EnforcementPlan}
 */
export function evaluateLockbox(pack, world) {
  const now = world.now instanceof Date ? world.now : new Date(world.now);
  const matchedRules = /** @type {EnforcementPlan['matchedRules']} */ ([]);
  const blockSet = new Set();
  const allowSet = new Set();

  for (const app of world.apps) {
    const decision = evaluateOneApp(app, pack, world, matchedRules);
    if (decision === "block") blockSet.add(app.id);
    else if (decision === "allow") allowSet.add(app.id);
  }

  const basePlan = {
    version: 1,
    computedAt: now.toISOString(),
    activeModeId: world.activeModeId,
    blockAppIds: Array.from(blockSet).sort(),
    allowAppIds: Array.from(allowSet).sort(),
    scheduleHints: pack.timeWindows.map((tw) => ({
      id: tw.id,
      label: tw.name,
      payload: {
        startHour: tw.startHour,
        startMinute: tw.startMinute,
        endHour: tw.endHour,
        endMinute: tw.endMinute,
      },
    })),
    matchedRules,
  };
  const withNeutrals = attachNeutralAsAllow(basePlan, world);
  return attachRestrictionTargets(withNeutrals, world.apps);
}

/**
 * @typedef {Object} AppEvalStep
 * @property {string} description
 * @property {'pass'|'block'|'allow'|'continue'} result
 */

/**
 * Single source of truth for per-app Lockbox rules (before neutral → allow attachment).
 * Keep `evaluateOneApp` and `buildDecisionTrace` aligned by routing through this only.
 *
 * @param {LockboxAppEntity} app
 * @param {LockboxRulepack} pack
 * @param {EvaluationWorld} world
 * @returns {{
 *   decision: 'allow'|'block'|'neutral',
 *   matchedRule: import('./types.js').EnforcementPlan['matchedRules'][number] | null,
 *   steps: AppEvalStep[],
 * }}
 */
export function runAppLockboxEvaluation(app, pack, world) {
  const now = world.now instanceof Date ? world.now : new Date(world.now);
  const nowM = minutesSinceMidnight(now);
  const emergencyActive =
    world.emergency?.active &&
    (!world.emergency.until || (world.emergency.until instanceof Date && world.emergency.until > now));

  /** @type {AppEvalStep[]} */
  const steps = [];
  const add = (/** @type {string} */ description, /** @type {AppEvalStep['result']} */ result) => {
    steps.push({ description, result });
  };

  const cat = app.classification.primaryCategory;
  const ov = app.classification.override;

  add(`Evaluate "${app.displayName}" (${app.id}) · category: ${cat} · mode: ${world.activeModeId}`, "continue");

  if (ov.kind === "force_allow") {
    add("Manual override: force allow (highest priority)", "allow");
    return {
      decision: "allow",
      matchedRule: { ruleId: "override:force_allow", reason: ov.reason || "User allow override", appId: app.id },
      steps,
    };
  }
  if (ov.kind === "force_block") {
    add("Manual override: force block", "block");
    return {
      decision: "block",
      matchedRule: { ruleId: "override:force_block", reason: ov.reason || "User block override", appId: app.id },
      steps,
    };
  }

  if (app.classification.userFlaggedSeverity) {
    add("User flagged: disturbing or explicit content", "block");
    return {
      decision: "block",
      matchedRule: {
        ruleId: "user:disturbing_flag",
        reason: "User blocked — disturbing content",
        appId: app.id,
      },
      steps,
    };
  }

  if (app.classification.contentSeverity === "flagged" && app.classification.severityAutoBlocked) {
    add("Auto-block: flagged content severity (all modes)", "block");
    return {
      decision: "block",
      matchedRule: {
        ruleId: "severity:auto_block",
        reason: "Disturbing or explicit content detected",
        appId: app.id,
      },
      steps,
    };
  }

  if (emergencyActive && pack.emergencyPolicy.strategy === "pause_automatic_blocks") {
    add("Emergency: automatic category blocks paused", "allow");
    return {
      decision: "allow",
      matchedRule: {
        ruleId: "emergency:pause",
        reason: "Emergency active — automatic category blocks paused",
        appId: app.id,
      },
      steps,
    };
  }

  if (emergencyActive && pack.emergencyPolicy.strategy === "allow_all_except_force_block") {
    add("Emergency: widen allow (except manual force-block)", "allow");
    return {
      decision: "allow",
      matchedRule: { ruleId: "emergency:allow_most", reason: "Emergency widen allow", appId: app.id },
      steps,
    };
  }

  if (pack.alwaysAllowCategories.includes(cat)) {
    add(`Always-allow template includes category "${cat}"`, "allow");
    return {
      decision: "allow",
      matchedRule: {
        ruleId: "template:always_allow_category",
        reason: `Category always allowed: ${cat}`,
        appId: app.id,
      },
      steps,
    };
  }

  const modeRule = pack.modeStrict.find((m) => m.modeId === world.activeModeId);
  if (modeRule?.extraBlockCategories?.includes(cat)) {
    add(`Stricter mode "${world.activeModeId}" blocks category "${cat}"`, "block");
    return {
      decision: "block",
      matchedRule: {
        ruleId: `mode_strict:${world.activeModeId}`,
        reason: `Work/stricter mode blocks category: ${cat}`,
        appId: app.id,
      },
      steps,
    };
  }

  for (const tw of pack.timeWindows) {
    if (tw.onlyInModes?.length && !tw.onlyInModes.includes(world.activeModeId)) {
      add(`Time window "${tw.name}" skipped (not active in mode ${world.activeModeId})`, "continue");
      continue;
    }
    const startM = tw.startHour * 60 + tw.startMinute;
    const endM = tw.endHour * 60 + tw.endMinute + 1;
    const inWin = inTimeWindow(startM, endM, nowM);
    const pad = (/** @type {number} */ n) => (n < 10 ? `0${n}` : String(n));
    const startLabel = `${tw.startHour}:${pad(tw.startMinute)}`;
    const endLabel = `${tw.endHour}:${pad(tw.endMinute)}`;
    add(`Time window "${tw.name}" (${startLabel}–${endLabel}) — ${inWin ? "active now" : "not active now"}`, "continue");
    if (!inWin) continue;
    if (tw.blockCategories.includes(cat)) {
      add(`Window blocks category "${cat}"`, "block");
      return {
        decision: "block",
        matchedRule: { ruleId: `time_window:${tw.id}`, reason: tw.name, appId: app.id },
        steps,
      };
    }
  }

  if (pack.alwaysBlockCategories.includes(cat)) {
    add(`Always-block template includes category "${cat}"`, "block");
    return {
      decision: "block",
      matchedRule: {
        ruleId: "template:always_block_category",
        reason: `Category always blocked: ${cat}`,
        appId: app.id,
      },
      steps,
    };
  }

  add("No automatic block rule matched (neutral before default policy)", "continue");
  add("Default posture: allow unless explicitly restricted", "allow");
  return { decision: "neutral", matchedRule: null, steps };
}

/**
 * @param {LockboxAppEntity} app
 * @param {LockboxRulepack} pack
 * @param {EvaluationWorld} world
 * @param {EnforcementPlan['matchedRules']} matchedRules
 * @returns {'allow'|'block'|'neutral'}
 */
function evaluateOneApp(app, pack, world, matchedRules) {
  const { decision, matchedRule } = runAppLockboxEvaluation(app, pack, world);
  if (matchedRule) matchedRules.push(matchedRule);
  return decision;
}

/**
 * Reconcile neutral → default allow (product posture: unknown apps not hard-blocked unless listed).
 * @param {EnforcementPlan} plan
 * @param {EvaluationWorld} world
 */
export function attachNeutralAsAllow(plan, world) {
  const blocked = new Set(plan.blockAppIds);
  const allowed = new Set(plan.allowAppIds);
  const matchedRules = [...plan.matchedRules];
  for (const app of world.apps) {
    if (blocked.has(app.id) || allowed.has(app.id)) continue;
    matchedRules.push({
      ruleId: "default:allow",
      reason: "No matching block rule — default allow",
      appId: app.id,
    });
    allowed.add(app.id);
  }
  return {
    ...plan,
    allowAppIds: Array.from(allowed).sort(),
    matchedRules,
  };
}
